// The answer control for "place the number" questions (22 Oct 2026, Year 2):
// the number line, tapped where the number goes, exactly as for "tap a point
// on the number line" (practice-number-line.tsx), with two big buttons under
// it that move the dot one tick at a time. A tick is narrow to tap on a
// phone; the buttons are not. The tick to find carries no number on the line
// (the loader refuses one that does), and nothing printed on the screen says
// where the dot is. Wording for the owner's approval
// (docs/year-2-answer-types-wording.md).
import { movePlace } from '../../platform/content/placeAnswer';
import { NumberLineView } from '../../platform/content/practice-number-line';
import {
  numberLineTickSpoken,
  type NumberLineSpec,
} from '../../platform/content/practice-number-line-data';

type Props = {
  line: NumberLineSpec;
  // The chosen tick, counted in the line's parts, or null for none yet.
  chosen: number | null;
  disabled: boolean;
  onChoose: (at: number | null) => void;
};

// Where the dot is, as the tick it is on is named (2 Nov 2026): "5" for a
// tick with a printed number, "the 8th tick from the left" for one without.
// Never the hidden value of an unnumbered tick, which is the answer.
function spokenPlace(line: NumberLineSpec, at: number): string {
  const tick = numberLineTickSpoken(line, at);
  return tick.endsWith('from the left') ? `the ${tick}` : tick;
}

export function PlaceAnswer({ line, chosen, disabled, onChoose }: Props) {
  return (
    <div className="grid gap-3">
      <NumberLineView
        line={line}
        chosen={chosen}
        disabled={disabled}
        onChoose={onChoose}
      />
      <div
        role="group"
        aria-label="Move the dot"
        className="mx-auto grid w-full max-w-sm grid-cols-2 gap-2"
      >
        <button
          type="button"
          className="dm-btn-secondary min-h-12"
          disabled={disabled}
          onClick={() => {
            onChoose(movePlace(line, chosen, -1));
          }}
        >
          <span aria-hidden="true">← </span>Move left
        </button>
        <button
          type="button"
          className="dm-btn-secondary min-h-12"
          disabled={disabled}
          onClick={() => {
            onChoose(movePlace(line, chosen, 1));
          }}
        >
          Move right<span aria-hidden="true"> →</span>
        </button>
      </div>
      {/* For a screen reader only: the tick the dot is on, named as the
          line's own tick buttons name it, so this tells it nothing the
          line does not. */}
      <p role="status" className="sr-only">
        {chosen === null ? '' : `The dot is on ${spokenPlace(line, chosen)}.`}
      </p>
    </div>
  );
}
